import * as React from 'react'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'

import * as api from '@owl-nest/api-client/next'
import { t, tc, tp } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as env from '@owl-nest/config'

type ShippingsProps = {
  zone: Omit<api.ShippingZone, 'id'> & {
    id: number | undefined
  }

  currency: string
  pickupDescription?: boolean
}

export function Shippings({ zone, currency, pickupDescription = false }: ShippingsProps) {
  const userConfig = env.useUserEnv()

  const homeDeliveryBands = zone.home_delivery?.bands?.map((band, index) => {
    return {
      id: band.id,
      node: getText(band, zone.home_delivery?.band_type, zone.home_delivery?.bands[index + 1]),
    }
  })

  const mondialRelayBands = zone.mondial_relay_delivery?.bands.map((band, index) => {
    return {
      id: band.id,
      node: getText(band, zone.mondial_relay_delivery?.band_type, zone.mondial_relay_delivery?.bands[index + 1]),
    }
  })

  return (
    <>
      <Grid>
        {homeDeliveryBands && <ShippingItem name={t('At home')} items={homeDeliveryBands} />}
        {mondialRelayBands && <ShippingItem name={t('Mondial Relay')} items={mondialRelayBands} />}
        {zone.pickup_points && <PickupPointShippingItem name={t('Pickup points')} />}
      </Grid>

      {zone.pickup_points && (
        <>
          {zone.pickup_points.map((pickupPoint) => {
            return [
              <PickupPoint>
                <PickupName key="pickup-heading">{models.pickup.city(pickupPoint)}</PickupName>
                {pickupDescription && (
                  <>
                    <PickupDescription key="pickup-description">
                      {models.pickup.description(pickupPoint)}
                    </PickupDescription>

                    <PickupAddressLink key="pickup-address-preview">
                      {models.pickup.address(pickupPoint) && (
                        <plume.Link
                          kind="secondary"
                          onClick={() =>
                            window.open(
                              'https://www.google.com/maps/place/' + models.pickup.address(pickupPoint) + '/',
                              '_blank',
                            )
                          }
                        >
                          {t('See address')}
                        </plume.Link>
                      )}
                    </PickupAddressLink>
                  </>
                )}
              </PickupPoint>,
            ]
          })}
        </>
      )}
    </>
  )

  function getText(band: api.ShippingBand, type: api.BandType | undefined, nextBand?: api.ShippingBand) {
    if (type === 'priced_per_cart') {
      return (
        <>
          {band.price === 0 ? (
            tp('free', 'BO shipping - delivery')
          ) : (
            <plume.LocaleMoneyDisplayer localeFormat={userConfig.lang} amount={band.price} currency={currency} />
          )}
          {nextBand ? (
            <>
              <>&nbsp;</>
              {tc('up to [price]', {
                price: (
                  <plume.LocaleMoneyDisplayer
                    localeFormat={userConfig.lang}
                    amount={nextBand.threshold ? nextBand.threshold - 0.01 : undefined}
                    currency={currency}
                  />
                ),
              })}
            </>
          ) : (
            <>
              <>&nbsp;</>
              {tc('above [price]', {
                price: (
                  <plume.LocaleMoneyDisplayer
                    localeFormat={userConfig.lang}
                    amount={band.threshold ? band.threshold - 0.01 : undefined}
                    currency={currency}
                  />
                ),
              })}
            </>
          )}
        </>
      )
    } else {
      return (
        <>
          {band.price === 0 ? (
            tp('free', 'BO shipping - delivery')
          ) : (
            <>
              {tc('[price] per shipment', {
                price: (
                  <plume.LocaleMoneyDisplayer localeFormat={userConfig.lang} amount={band.price} currency={currency} />
                ),
              })}
            </>
          )}
          {Boolean(band.price_increase) && (
            <>
              <>&nbsp;</>
              {band.price === 0 ? t('then') : '+'}
              <>&nbsp;</>
              {tc('[price] per quantity', {
                price: (
                  <plume.LocaleMoneyDisplayer
                    localeFormat={userConfig.lang}
                    amount={band.price_increase}
                    currency={currency}
                  />
                ),
              })}
            </>
          )}
        </>
      )
    }
  }
}

const Grid = styled.div`
  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: grid;
    grid-column-gap: 20px;
    grid-row-gap: 16px;
    grid-template-columns: repeat(2, max-content);
  }
`

type ItemProps = {
  name: string
  items: { id: number; node: React.ReactNode }[]
}

export function ShippingItem({ name, items }: ItemProps) {
  return (
    <>
      <plume.styles.copy.M>{name}</plume.styles.copy.M>
      <List>
        {items.map((item) => (
          <li key={item.id}>
            <ShippingCost>{item.node}</ShippingCost>
          </li>
        ))}
      </List>
    </>
  )
}

function PickupPointShippingItem({ name }: { name: string }) {
  return (
    <>
      <plume.styles.copy.M>{name}</plume.styles.copy.M>

      <ShippingCost>{t('Free')}</ShippingCost>
    </>
  )
}

const List = styled.ul`
  list-style-type: none;
  margin: 0 0 16px 0;
  padding: 0;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin: 0;
  }
`

const ShippingCost = styled(plume.styles.copy.S)`
  display: flex;
  align-items: baseline;
  color: ${plume.COLORS.PRIMARY_SAND_700};

  @media not screen and ${plume.BREAKPOINTS.MOBILE_L} {
    margin-left: 24px;
  }
`

const PickupPoint = styled.div`
  margin-left: 32px;
  margin-top: 8px;
`

const PickupName = styled(plume.styles.copy.S)`
  font-weight: 600; /* HACK: Irregular font manipulation. We'll be adding copy.M.semiBold, so this is just by anticipation. */
`

const PickupDescription = styled(plume.styles.copy.S)`
  color: ${plume.COLORS.PRIMARY_SAND_900};
`

const PickupAddressLink = styled(plume.styles.copy.S)``
